import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BookOpen,
  User,
  Search,
  X,
  CheckCircle,
  AlertCircle,
  Loader,
  Book,
  ArrowRight,
  Info,
  IndianRupee,
  Shield,
  AlertTriangle,
  ChevronRight,
  Mail,
  Hash,
  Sparkles,
  Check,
  ChevronLeft,
  Users,
  Calendar,
  BadgeCheck,
  Scan,
  Copy,
  BookMarked,
  RefreshCw
} from 'lucide-react';
import api from '../api';
import BarcodeScanner from './components/BarcodeScanner';

const IssueBook = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  const [memberData, setMemberData] = useState(null);
  const [bookData, setBookData] = useState(null);
  const [dueDays, setDueDays] = useState(14);
  const [notes, setNotes] = useState('');
  const [issuing, setIssuing] = useState(false);
  
  const [showScanner, setShowScanner] = useState(false);
  const [scanType, setScanType] = useState('member');
  
  const [memberSearch, setMemberSearch] = useState('');
  const [bookSearch, setBookSearch] = useState('');
  const [memberSearchResults, setMemberSearchResults] = useState([]);
  const [bookSearchResults, setBookSearchResults] = useState([]);
  const [showMemberResults, setShowMemberResults] = useState(false);
  const [showBookResults, setShowBookResults] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  
  const [rules, setRules] = useState(null);
  const [memberActiveBooks, setMemberActiveBooks] = useState([]);
  const [isBarcodeScanning, setIsBarcodeScanning] = useState(false);
  const [memberStats, setMemberStats] = useState({
    totalIssued: 0,
    overdueCount: 0,
    pendingFines: 0,
    maxAllowed: 5,
    remaining: 5
  });
  const [selectedCopy, setSelectedCopy] = useState(null);
  const [availableCopies, setAvailableCopies] = useState([]);
  const [isCheckingMember, setIsCheckingMember] = useState(false);
  
  const memberInputRef = useRef(null);
  const bookInputRef = useRef(null);
  const memberSearchTimeoutRef = useRef(null);
  const bookSearchTimeoutRef = useRef(null);
  const memberResultsRef = useRef(null);
  const bookResultsRef = useRef(null);
  
  const tenantId = localStorage.getItem('tenantId');

  // ==================== BARCODE SCAN HANDLER ====================
  const handleBarcodeScan = useCallback(async (barcodeValue) => {
    if (!barcodeValue || barcodeValue.trim() === '') {
      setError('Please scan a valid barcode');
      setTimeout(() => setError(null), 3000);
      return;
    }

    setIsBarcodeScanning(true);
    setError(null);

    try {
      if (scanType === 'member') {
        const response = await api.get('/api/library/members/barcode', {
          params: { tenantId, barcode: barcodeValue }
        });

        if (!response.data.success || !response.data.data) {
          const searchResponse = await api.get('/api/library/members/search', {
            params: { 
              tenantId, 
              query: barcodeValue,
              limit: 5,
              activeOnly: true
            }
          });
          
          if (searchResponse.data.success && searchResponse.data.data.length > 0) {
            const member = searchResponse.data.data[0];
            selectMember(member);
            setSuccess(`✅ Library member found: ${member.name}`);
            setTimeout(() => setSuccess(null), 3000);
            setIsBarcodeScanning(false);
            setShowScanner(false);
            return;
          }
          
          throw new Error('Member record not found');
        }

        const member = response.data.data;
        selectMember(member);
        setSuccess(`✅ Library member found: ${member.name}`);
        setTimeout(() => setSuccess(null), 3000);
      } else if (scanType === 'book') {
        const response = await api.get('/api/books/barcode', {
          params: { tenantId, barcode: barcodeValue }
        });

        if (!response.data.success || !response.data.data) {
          const searchResponse = await api.get('/api/books/search', {
            params: { 
              tenantId, 
              search: barcodeValue,
              limit: 5
            }
          });
          
          if (searchResponse.data.success && searchResponse.data.data.length > 0) {
            const book = searchResponse.data.data[0];
            if (book.availableCopies <= 0) {
              setError('❌ All copies of this book are currently issued');
              setTimeout(() => setError(null), 5000);
              setIsBarcodeScanning(false);
              return;
            }
            selectBook(book);
            setIsBarcodeScanning(false);
            setShowScanner(false);
            return;
          }
          
          throw new Error('Book record not found');
        }

        const book = response.data.data.book;
        if (book.availableCopies <= 0) {
          setError('❌ All copies of this book are currently issued');
          setTimeout(() => setError(null), 5000);
          setIsBarcodeScanning(false);
          return;
        }
        selectBook(book);
        setIsBarcodeScanning(false);
        setShowScanner(false);
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Record not found. Please check details and try again.';
      setError(`❌ ${errorMessage}`);
      setTimeout(() => setError(null), 5000);
    } finally {
      setIsBarcodeScanning(false);
      setShowScanner(false);
    }
  }, [tenantId, scanType]);

  // ==================== HARDWARE SCANNER SUPPORT ====================
  const handleScannerInput = useCallback((barcode) => {
    if (!barcode || barcode.trim() === '') return;
    
    const trimmedBarcode = barcode.trim();
    
    if (step === 1 && !memberData) {
      setMemberSearch(trimmedBarcode);
      setTimeout(() => handleBarcodeScan(trimmedBarcode), 300);
    } else if (step === 2 && memberData && !bookData) {
      setBookSearch(trimmedBarcode);
      setTimeout(() => handleBarcodeScan(trimmedBarcode), 300);
    }
  }, [step, memberData, bookData, handleBarcodeScan]);

  useEffect(() => {
    let buffer = '';
    let timeout = null;
    let lastKeyTime = 0;

    const handleKeyDown = (event) => {
      const target = event.target;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT') {
        return;
      }

      const key = event.key;

      if (key === 'Enter') {
        if (buffer.trim()) {
          event.preventDefault();
          const barcode = buffer.trim();
          console.log('📷 Hardware scanner detected:', barcode);
          handleScannerInput(barcode);
          buffer = '';
        }
        return;
      }

      if (key.length === 1 && key.match(/[a-zA-Z0-9\-_./]/)) {
        if (timeout) {
          clearTimeout(timeout);
        }

        const now = Date.now();
        const timeDiff = now - lastKeyTime;
        lastKeyTime = now;

        buffer += key;

        if (timeDiff < 50 && buffer.length > 3) {
          timeout = setTimeout(() => {
            if (buffer.trim()) {
              const barcode = buffer.trim();
              console.log('📷 Hardware scanner auto-detected:', barcode);
              handleScannerInput(barcode);
              buffer = '';
            }
            timeout = null;
          }, 150);
        } else {
          timeout = setTimeout(() => {
            if (buffer.trim()) {
              const barcode = buffer.trim();
              console.log('📷 Manual entry detected:', barcode);
              handleScannerInput(barcode);
              buffer = '';
            }
            timeout = null;
          }, 500);
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [handleScannerInput]);

  // ==================== FALLBACK RULES ====================
  const getFallbackRules = useCallback((role) => {
    const fallbacks = {
      student: {
        maxBooksAllowed: 5,
        defaultIssueDays: 15,
        finePerDay: 2,
        maxFineCap: 500,
        gracePeriodDays: 2,
        maxRenewals: 2,
        renewalDays: 7
      },
      staff: {
        maxBooksAllowed: 10,
        defaultIssueDays: 50,
        finePerDay: 0,
        maxFineCap: 500,
        gracePeriodDays: 2,
        maxRenewals: 2,
        renewalDays: 7
      },
      faculty: {
        maxBooksAllowed: 10,
        defaultIssueDays: 50,
        finePerDay: 0,
        maxFineCap: 500,
        gracePeriodDays: 2,
        maxRenewals: 2,
        renewalDays: 7
      }
    };
    return fallbacks[role] || fallbacks.student;
  }, []);

  // ==================== FETCH MEMBER FULL DETAILS ====================
  const fetchMemberFullDetails = useCallback(async () => {
    // Use the user ID (memberData.userId) for fetching transactions
    const userId = memberData?.userId || memberData?.id;
    
    if (!userId) {
      console.error('❌ No user ID available for fetching transactions');
      return;
    }
    
    setIsCheckingMember(true);
    
    try {
      console.log(`📡 Fetching active transactions for user: ${userId}`);
      
      // 1. Fetch member's active transactions using the User ID
      const activeResponse = await api.get(`/api/transactions/members/${userId}/active`, {
        params: { tenantId }
      });
      
      console.log('📦 Active transactions response:', activeResponse.data);
      
      const activeBooks = activeResponse.data.data || [];
      setMemberActiveBooks(activeBooks);
      const activeCount = activeBooks.length;
      
      console.log(`✅ Active books count: ${activeCount}`);
      
      // 2. Fetch member's overdue books
      const overdueResponse = await api.get(`/api/transactions/members/${userId}/overdue`, {
        params: { tenantId }
      });
      const overdueCount = overdueResponse.data.data?.length || 0;
      
      // 3. Fetch member's pending fines
      const finesResponse = await api.get(`/api/transactions/members/${userId}/fines`, {
        params: { tenantId }
      });
      const pendingFines = finesResponse.data.data?.total || 0;
      
      // 4. Fetch member's rules based on role
      const role = memberData?.role || 'student';
      console.log(`📡 Fetching rules for role: ${role}, tenantId: ${tenantId}`);
      
      let maxAllowed = 5;
      let defaultDays = 15;
      let rulesData = null;
      
      try {
        const rulesResponse = await api.get(`/api/library/rules/${tenantId}/role/${role}`);
        console.log('📦 Rules API Response:', rulesResponse.data);
        
        if (rulesResponse.data?.status === 'success') {
          rulesData = rulesResponse.data.data.rules;
          maxAllowed = rulesData.maxBooksAllowed || 5;
          defaultDays = rulesData.defaultIssueDays || 15;
          setRules(rulesData);
          setDueDays(defaultDays);
          
          console.log(`✅ Rules loaded: maxBooksAllowed=${maxAllowed}, defaultIssueDays=${defaultDays}`);
        } else {
          console.warn('⚠️ Rules API returned unexpected response:', rulesResponse.data);
          const fallbackRules = getFallbackRules(role);
          maxAllowed = fallbackRules.maxBooksAllowed;
          defaultDays = fallbackRules.defaultIssueDays;
          setRules(fallbackRules);
          setDueDays(defaultDays);
        }
      } catch (rulesError) {
        console.error('❌ Error fetching rules:', rulesError);
        const fallbackRules = getFallbackRules(role);
        maxAllowed = fallbackRules.maxBooksAllowed;
        defaultDays = fallbackRules.defaultIssueDays;
        setRules(fallbackRules);
        setDueDays(defaultDays);
      }
      
      // Update member stats with all data
      setMemberStats({
        totalIssued: activeCount,
        overdueCount: overdueCount,
        pendingFines: pendingFines,
        maxAllowed: maxAllowed,
        remaining: Math.max(0, maxAllowed - activeCount)
      });
      
      console.log(`📊 Member Stats Updated: totalIssued=${activeCount}, maxAllowed=${maxAllowed}, remaining=${Math.max(0, maxAllowed - activeCount)}`);
      
    } catch (err) {
      console.error('❌ Error fetching member details:', err);
      // Set default values
      const maxAllowed = 5;
      setMemberStats({
        totalIssued: 0,
        overdueCount: 0,
        pendingFines: 0,
        maxAllowed: maxAllowed,
        remaining: maxAllowed
      });
    } finally {
      setIsCheckingMember(false);
    }
  }, [memberData, tenantId, getFallbackRules]);

  // Fetch member data when member is selected
  useEffect(() => {
    if (memberData && tenantId) {
      fetchMemberFullDetails();
    }
  }, [memberData, tenantId, fetchMemberFullDetails]);

  useEffect(() => {
    if (memberInputRef.current) {
      setTimeout(() => memberInputRef.current.focus(), 100);
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (memberResultsRef.current && !memberResultsRef.current.contains(event.target)) {
        setShowMemberResults(false);
      }
      if (bookResultsRef.current && !bookResultsRef.current.contains(event.target)) {
        setShowBookResults(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ==================== SEARCH FUNCTIONS ====================
  useEffect(() => {
    if (memberSearchTimeoutRef.current) {
      clearTimeout(memberSearchTimeoutRef.current);
    }

    if (!memberSearch || memberSearch.length < 2) {
      setMemberSearchResults([]);
      setShowMemberResults(false);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    memberSearchTimeoutRef.current = setTimeout(() => {
      performMemberSearch(memberSearch);
    }, 400);

    return () => {
      if (memberSearchTimeoutRef.current) {
        clearTimeout(memberSearchTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberSearch]);

  useEffect(() => {
    if (bookSearchTimeoutRef.current) {
      clearTimeout(bookSearchTimeoutRef.current);
    }

    if (!bookSearch || bookSearch.length < 2) {
      setBookSearchResults([]);
      setShowBookResults(false);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    bookSearchTimeoutRef.current = setTimeout(() => {
      performBookSearch(bookSearch);
    }, 400);

    return () => {
      if (bookSearchTimeoutRef.current) {
        clearTimeout(bookSearchTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookSearch]);

  const performMemberSearch = async (query) => {
    try {
      const response = await api.get('/api/library/members/search', {
        params: { 
          tenantId, 
          query, 
          limit: 15,
          activeOnly: true
        }
      });
      
      if (response.data.success) {
        setMemberSearchResults(response.data.data || []);
        setShowMemberResults(response.data.data.length > 0);
      } else {
        setMemberSearchResults([]);
        setShowMemberResults(false);
      }
    } catch (err) {
      console.error('Error searching members:', err);
      setMemberSearchResults([]);
      setShowMemberResults(false);
    } finally {
      setIsSearching(false);
    }
  };

  const performBookSearch = async (query) => {
    try {
      const response = await api.get('/api/books/search', {
        params: { 
          tenantId, 
          search: query, 
          limit: 15
        }
      });
      
      if (response.data.success) {
        setBookSearchResults(response.data.data || []);
        setShowBookResults(response.data.data.length > 0);
      } else {
        setBookSearchResults([]);
        setShowBookResults(false);
      }
    } catch (err) {
      console.error('Error searching books:', err);
      setBookSearchResults([]);
      setShowBookResults(false);
    } finally {
      setIsSearching(false);
    }
  };

  // ==================== SELECT FUNCTIONS ====================
  const selectMember = (member) => {
    // IMPORTANT: The member from search/scan has:
    // - id: LibraryMember _id (this is what the backend expects for libraryMemberId)
    // - userId: User _id (for other operations like fetching transactions)
    
    console.log('👤 Selecting member from search results:', member);
    
    // Find the user ID - it could be in different fields
    const userId = member.userId || member.id || member._id;
    const libraryMemberId = member.id || member._id;
    
    console.log('🔑 LibraryMember ID:', libraryMemberId);
    console.log('🔑 User ID:', userId);
    
    const mappedMemberData = {
      // LibraryMember _id - used for libraryMemberId in issue API
      libraryMemberId: libraryMemberId,
      // User _id - used for fetching transactions and member details
      userId: userId,
      // For backward compatibility
      id: userId,
      name: member.name || `${member.firstName || ''} ${member.lastName || ''}`.trim(),
      email: member.email || '',
      usn: member.usn || member.USN || '',
      role: member.role || 'student',
      libraryNumber: member.libraryNumber || '',
      className: member.className || '',
      phone: member.phone || '',
      isActive: member.isActive !== false
    };
    
    console.log('📋 Mapped member data for frontend:', mappedMemberData);
    
    setMemberData(mappedMemberData);
    setMemberSearch('');
    setMemberSearchResults([]);
    setShowMemberResults(false);
    setStep(2);
    if (bookInputRef.current) {
      setTimeout(() => bookInputRef.current.focus(), 100);
    }
  };

  const selectBook = (book) => {
    if (book.availableCopies <= 0) {
      setError('❌ All copies of this book are currently issued');
      setTimeout(() => setError(null), 5000);
      return;
    }
    
    const mappedBookData = {
      _id: book._id || book.id,
      id: book._id || book.id,
      title: book.title || '',
      author: book.author || '',
      masterBarcode: book.masterBarcode || book.barcode || '',
      isbn: book.isbn || '',
      category: book.category || 'General',
      totalCopies: book.totalCopies || 0,
      availableCopies: book.availableCopies || 0,
      copies: book.copies || []
    };
    
    setBookData(mappedBookData);
    const available = mappedBookData.copies?.filter(c => c.status === 'available') || [];
    setAvailableCopies(available);
    setBookSearch('');
    setBookSearchResults([]);
    setShowBookResults(false);
    
    if (available.length === 1) {
      setSelectedCopy(available[0]);
    } else {
      setSelectedCopy(null);
    }
    
    setStep(3);
  };

  const selectCopy = (copy) => {
    setSelectedCopy(copy);
  };

  // ==================== ISSUE BOOK ====================
  const handleIssue = async () => {
    if (!memberData || !bookData) {
      setError('❌ Please select both a library member and a book');
      return;
    }

    const activeCount = memberActiveBooks.length || 0;
    const maxAllowed = memberStats.maxAllowed || rules?.maxBooksAllowed || 5;
    
    if (activeCount >= maxAllowed) {
      setError(`❌ Member has reached their max borrowing limit (${maxAllowed} books)`);
      setTimeout(() => setError(null), 5000);
      return;
    }

    const hasOverdue = memberActiveBooks.some(b => b.isOverdue);
    if (hasOverdue) {
      setError('❌ Member has overdue books. Please process returns first.');
      setTimeout(() => setError(null), 5000);
      return;
    }

    if (memberStats.pendingFines > 0) {
      setError(`❌ Member has unpaid fines of ₹${memberStats.pendingFines}. Please collect fines first.`);
      setTimeout(() => setError(null), 5000);
      return;
    }

    if (bookData.availableCopies <= 0) {
      setError('❌ No available copies left for this book');
      setTimeout(() => setError(null), 5000);
      return;
    }

    if (availableCopies.length > 1 && !selectedCopy) {
      setError('❌ Please select a specific copy barcode to issue');
      setTimeout(() => setError(null), 5000);
      return;
    }

    let copyToIssue = selectedCopy;
    if (availableCopies.length === 1 && !selectedCopy) {
      copyToIssue = availableCopies[0];
      setSelectedCopy(copyToIssue);
    }

    setIssuing(true);
    setError(null);

    try {
      // CRITICAL: Use libraryMemberId (LibraryMember _id) for the transaction
      // The backend's getMemberDetails expects the LibraryMember document's _id
      const libraryMemberId = memberData.libraryMemberId || memberData.id;
      
      // The book ID should be the book's _id
      const bookId = bookData._id || bookData.id;
      
      console.log('🔑 Issuing with libraryMemberId:', libraryMemberId);
      console.log('📚 Book ID:', bookId);
      console.log('📋 Copy barcode:', copyToIssue?.barcode || bookData.masterBarcode || bookData.barcode);
      
      const payload = {
        tenantId,
        bookId: bookId,
        libraryMemberId: libraryMemberId,
        dueDays: dueDays,
        barcode: copyToIssue?.barcode || bookData.masterBarcode || bookData.barcode,
        notes: notes
      };

      console.log('📦 Final issue payload:', payload);

      // Use the correct API endpoint - note the base URL is already in api config
      const response = await api.post('/api/transactions/issue', payload);

      console.log('📦 Issue response:', response.data);

      if (response.data.success) {
        // Update local state
        setBookData(prev => ({
          ...prev,
          availableCopies: prev.availableCopies - 1
        }));
        
        setMemberActiveBooks(prev => [...prev, response.data.data.transaction]);
        setMemberStats(prev => ({
          ...prev,
          totalIssued: prev.totalIssued + 1,
          remaining: Math.max(0, prev.remaining - 1)
        }));

        setSuccess(`✅ Successfully issued "${bookData.title}" to ${memberData.name}. Return due by: ${new Date(response.data.data.dueDate).toLocaleDateString('en-IN')}`);
        setTimeout(() => {
          resetForm();
        }, 5000);
      }
    } catch (err) {
      console.error('❌ Issue error:', err);
      console.error('❌ Error response:', err.response?.data);
      console.error('❌ Error status:', err.response?.status);
      
      // Check if it's a 404 error (route not found)
      if (err.response?.status === 404) {
        setError('❌ API endpoint not found. Please check the server configuration.');
      } else {
        const errorMsg = err.response?.data?.message || err.message || 'Could not issue book. Try again.';
        setError(`❌ ${errorMsg}`);
      }
      setTimeout(() => setError(null), 5000);
    } finally {
      setIssuing(false);
    }
  };

  const resetForm = () => {
    setMemberData(null);
    setBookData(null);
    setDueDays(14);
    setNotes('');
    setStep(1);
    setError(null);
    setSuccess(null);
    setMemberSearchResults([]);
    setBookSearchResults([]);
    setShowMemberResults(false);
    setShowBookResults(false);
    setMemberActiveBooks([]);
    setRules(null);
    setMemberSearch('');
    setBookSearch('');
    setSelectedCopy(null);
    setAvailableCopies([]);
    setMemberStats({
      totalIssued: 0,
      overdueCount: 0,
      pendingFines: 0,
      maxAllowed: 5,
      remaining: 5
    });
    if (memberInputRef.current) {
      setTimeout(() => memberInputRef.current.focus(), 100);
    }
  };

  const goBack = () => {
    if (step === 2) {
      setStep(1);
      setMemberData(null);
      setMemberSearch('');
      setMemberSearchResults([]);
      setShowMemberResults(false);
      if (memberInputRef.current) {
        setTimeout(() => memberInputRef.current.focus(), 100);
      }
    } else if (step === 3) {
      setStep(2);
      setBookData(null);
      setBookSearch('');
      setBookSearchResults([]);
      setShowBookResults(false);
      setSelectedCopy(null);
      setAvailableCopies([]);
      if (bookInputRef.current) {
        setTimeout(() => bookInputRef.current.focus(), 100);
      }
    }
  };

  const canBorrow = () => {
    const activeCount = memberActiveBooks.length || 0;
    const maxAllowed = memberStats.maxAllowed || rules?.maxBooksAllowed || 5;
    return activeCount < maxAllowed;
  };

  const getBorrowingStatus = () => {
    const activeCount = memberActiveBooks.length || 0;
    const maxAllowed = memberStats.maxAllowed || rules?.maxBooksAllowed || 5;
    if (activeCount >= maxAllowed) return 'full';
    if (activeCount >= maxAllowed * 0.8) return 'warning';
    return 'ok';
  };

  const highlightMatch = (text, query) => {
    if (!query || !text || typeof text !== 'string') return text || '';
    const index = text.toLowerCase().indexOf(query.toLowerCase());
    if (index === -1) return text;
    return (
      <>
        {text.substring(0, index)}
        <span className="bg-amber-200 dark:bg-amber-800/60 text-slate-900 dark:text-white font-semibold">
          {text.substring(index, index + query.length)}
        </span>
        {text.substring(index + query.length)}
      </>
    );
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      student: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300',
      faculty: 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300',
      teacher: 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300',
      staff: 'bg-slate-200 text-slate-800 dark:bg-gray-700 dark:text-slate-200',
      hod: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-300',
      principal: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
      librarian: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300'
    };
    return colors[role] || colors.student;
  };

  // Get copy availability status
  const getCopyStatus = (copy) => {
    if (copy.isIssued) return { label: 'Issued', color: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300', icon: BookMarked };
    if (copy.isLost) return { label: 'Lost', color: 'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-300', icon: AlertTriangle };
    if (copy.isDamaged) return { label: 'Damaged', color: 'bg-slate-200 text-slate-800 dark:bg-gray-700 dark:text-slate-200', icon: AlertCircle };
    return { label: 'Available', color: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300', icon: CheckCircle };
  };

  // ==================== SCANNER CONTROLS ====================
  const openMemberScanner = () => {
    setScanType('member');
    setShowScanner(true);
  };

  const openBookScanner = () => {
    setScanType('book');
    setShowScanner(true);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 p-3 sm:p-4 md:p-6">
      <div className="max-w-6xl mx-auto">
        
        {/* Header Bar */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pb-4 mb-4 border-b-2 border-slate-200 dark:border-gray-700">
          <div className="flex items-center space-x-3">
            <div className="p-2.5 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-xl shadow-lg shadow-blue-500/20">
              <BookOpen className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
            </div>
            <div>
              <h1 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 dark:text-white flex items-center">
                Issue Book Counter
                <span className="ml-2 text-xs font-medium bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 px-2.5 py-0.5 rounded-full">
                  New
                </span>
              </h1>
              <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 mt-0.5">
                Scan student ID or search to assign book copies
              </p>
            </div>
          </div>
          
          <button
            onClick={() => navigate('/librarian/transactions')}
            className="self-start sm:self-auto px-4 py-2 bg-white dark:bg-gray-800 border-2 border-slate-200 dark:border-gray-700 hover:border-blue-500 dark:hover:border-blue-500 text-slate-700 dark:text-slate-300 rounded-xl text-xs sm:text-sm font-semibold transition-all flex items-center space-x-2 shadow-sm hover:shadow-md"
          >
            <span>View Records</span>
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>

        {/* Stepper Navigation */}
        <div className="bg-white dark:bg-gray-800 p-3 sm:p-4 rounded-2xl border border-slate-200 dark:border-gray-700 shadow-sm mb-4 sm:mb-6">
          <div className="flex items-center justify-between max-w-2xl mx-auto">
            {[
              { num: 1, label: 'Borrower', icon: User },
              { num: 2, label: 'Select Book', icon: Book },
              { num: 3, label: 'Confirm', icon: CheckCircle }
            ].map((item, index) => (
              <React.Fragment key={item.num}>
                <div className="flex flex-col items-center">
                  <div className={`
                    w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center text-sm sm:text-base font-bold transition-all duration-300
                    ${step >= item.num 
                      ? 'bg-gradient-to-br from-blue-600 to-indigo-600 text-white shadow-lg shadow-blue-500/30 scale-100' 
                      : 'bg-slate-100 dark:bg-gray-700 text-slate-400 dark:text-slate-500 scale-95'
                    }
                  `}>
                    {step > item.num ? (
                      <Check className="h-5 w-5 sm:h-6 sm:w-6" />
                    ) : (
                      <item.icon className="h-4 w-4 sm:h-5 sm:w-5" />
                    )}
                  </div>
                  <span className={`text-[10px] sm:text-xs font-semibold mt-1.5 transition-colors ${
                    step >= item.num 
                      ? 'text-blue-600 dark:text-blue-400' 
                      : 'text-slate-400 dark:text-slate-500'
                  }`}>
                    {item.label}
                  </span>
                </div>
                {index < 2 && (
                  <div className={`flex-1 h-0.5 mx-1 sm:mx-2 transition-all duration-300 ${
                    step > item.num 
                      ? 'bg-gradient-to-r from-blue-600 to-indigo-600' 
                      : 'bg-slate-200 dark:bg-gray-700'
                  }`} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Dynamic Alerts */}
        {success && (
          <div className="mb-4 bg-gradient-to-r from-emerald-50 to-emerald-100/70 dark:from-emerald-900/30 dark:to-emerald-800/20 border border-emerald-200 dark:border-emerald-800 rounded-2xl p-4 text-emerald-800 dark:text-emerald-300 flex items-center justify-between shadow-sm animate-slideDown">
            <span className="flex items-center text-xs sm:text-sm font-medium">
              <div className="p-1.5 bg-emerald-600 rounded-full mr-3">
                <CheckCircle className="h-4 w-4 text-white" />
              </div>
              {success}
            </span>
            <button onClick={() => setSuccess(null)} className="text-emerald-700 dark:text-emerald-300 hover:opacity-75 p-1.5 hover:bg-emerald-200/50 dark:hover:bg-emerald-800/30 rounded-lg transition-colors">
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        {error && (
          <div className="mb-4 bg-gradient-to-r from-rose-50 to-rose-100/70 dark:from-rose-900/30 dark:to-rose-800/20 border border-rose-200 dark:border-rose-800 rounded-2xl p-4 text-rose-800 dark:text-rose-300 flex items-center justify-between shadow-sm animate-slideDown">
            <span className="flex items-center text-xs sm:text-sm font-medium">
              <div className="p-1.5 bg-rose-600 rounded-full mr-3">
                <AlertCircle className="h-4 w-4 text-white" />
              </div>
              {error}
            </span>
            <button onClick={() => setError(null)} className="text-rose-700 dark:text-rose-300 hover:opacity-75 p-1.5 hover:bg-rose-200/50 dark:hover:bg-rose-800/30 rounded-lg transition-colors">
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        {/* STEP 1: MEMBER SEARCH */}
        {step === 1 && (
          <div className="bg-white dark:bg-gray-800 rounded-2xl border border-slate-200 dark:border-gray-700 p-4 sm:p-6 md:p-8 shadow-lg shadow-slate-200/50 dark:shadow-none">
            <div className="flex items-center space-x-3 mb-6">
              <div className="p-2 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl">
                <User className="h-5 w-5 text-white" />
              </div>
              <div>
                <h2 className="font-bold text-gray-900 dark:text-white text-base sm:text-lg">Find Library Member</h2>
                <p className="text-xs text-slate-500 dark:text-slate-400">Scan card or search by name / USN</p>
              </div>
            </div>

            {/* Scan Button */}
            <button
              onClick={openMemberScanner}
              disabled={isBarcodeScanning}
              className="w-full py-3.5 sm:py-4 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 active:from-blue-800 active:to-indigo-800 disabled:opacity-50 text-white rounded-2xl font-semibold transition-all flex items-center justify-center space-x-3 shadow-lg shadow-blue-500/25 text-sm sm:text-base"
            >
              {isBarcodeScanning ? (
                <Loader className="h-5 w-5 animate-spin" />
              ) : (
                <Scan className="h-5 w-5" />
              )}
              <span>{isBarcodeScanning ? 'Reading Scanner...' : 'Scan Member Card / Barcode'}</span>
            </button>

            <div className="relative flex items-center justify-center my-5">
              <div className="w-full border-t border-slate-200 dark:border-gray-700"></div>
              <span className="absolute bg-white dark:bg-gray-800 px-4 text-xs font-semibold text-slate-400 uppercase tracking-wider">or search manually</span>
            </div>

            {/* Search Input */}
            <div className="relative" ref={memberResultsRef}>
              <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-2">
                Search by Name, USN, or Email
                {isSearching && <span className="ml-2 text-blue-500 animate-pulse font-normal">(Searching...)</span>}
              </label>
              
              <div className="relative group">
                <div className="absolute left-4 top-1/2 transform -translate-y-1/2">
                  <Search className="h-4 w-4 text-slate-400 group-focus-within:text-blue-500 transition-colors" />
                </div>
                <input
                  ref={memberInputRef}
                  type="text"
                  placeholder="Type name, USN, or email..."
                  value={memberSearch}
                  onChange={(e) => setMemberSearch(e.target.value)}
                  className="w-full pl-11 pr-11 py-3.5 rounded-2xl border-2 border-slate-200 dark:border-gray-700 bg-slate-50/50 dark:bg-gray-700/50 text-gray-900 dark:text-white text-sm focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 transition-all placeholder:text-slate-400"
                />
                {memberSearch && (
                  <button
                    onClick={() => {
                      setMemberSearch('');
                      setMemberSearchResults([]);
                      setShowMemberResults(false);
                      if (memberInputRef.current) memberInputRef.current.focus();
                    }}
                    className="absolute right-3.5 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 p-1.5 hover:bg-slate-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>

              {/* Dropdown Results */}
              {showMemberResults && memberSearchResults.length > 0 && (
                <div className="absolute z-50 left-0 right-0 mt-2 bg-white dark:bg-gray-800 rounded-2xl border-2 border-slate-200 dark:border-gray-700 shadow-2xl max-h-80 overflow-y-auto divide-y divide-slate-100 dark:divide-gray-700">
                  {memberSearchResults.map((member, index) => (
                    <button
                      key={member.id || index}
                      onClick={() => selectMember(member)}
                      className="w-full text-left px-4 py-3.5 hover:bg-blue-50/80 dark:hover:bg-gray-700/70 transition-all flex items-center justify-between group"
                    >
                      <div className="flex items-center space-x-3 min-w-0 pr-2">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-600 to-indigo-600 text-white font-bold text-sm flex items-center justify-center flex-shrink-0 shadow-md">
                          {member.name?.charAt(0) || 'U'}
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                            {highlightMatch(member.name, memberSearch)}
                          </p>
                          <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                            {member.usn && (
                              <span className="font-mono flex items-center bg-slate-100 dark:bg-gray-700 px-1.5 py-0.5 rounded">
                                <Hash className="h-3 w-3 mr-0.5" />
                                {highlightMatch(member.usn, memberSearch)}
                              </span>
                            )}
                            {member.email && (
                              <span className="truncate flex items-center">
                                <Mail className="h-3 w-3 mr-0.5" />
                                {highlightMatch(member.email, memberSearch)}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center space-x-2 flex-shrink-0">
                        <span className={`px-2.5 py-1 text-xs font-semibold rounded-full capitalize ${getRoleBadgeColor(member.role)}`}>
                          {member.role}
                        </span>
                        <ChevronRight className="h-4 w-4 text-slate-400 group-hover:text-blue-600 transition-colors" />
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {showMemberResults && memberSearchResults.length === 0 && memberSearch.length >= 2 && (
                <div className="absolute z-50 left-0 right-0 mt-2 bg-white dark:bg-gray-800 rounded-2xl border-2 border-slate-200 dark:border-gray-700 p-6 shadow-2xl text-center">
                  <div className="p-3 bg-slate-100 dark:bg-gray-700 rounded-full w-fit mx-auto mb-3">
                    <Users className="h-6 w-6 text-slate-400" />
                  </div>
                  <p className="text-sm font-semibold text-slate-600 dark:text-slate-300">No member found</p>
                  <p className="text-xs text-slate-400 mt-1">Double check the USN or name spelling</p>
                </div>
              )}
            </div>

            {/* Tips */}
            <div className="mt-5 p-4 bg-gradient-to-r from-blue-50/80 to-indigo-50/80 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-2xl border border-blue-200 dark:border-blue-800/50">
              <p className="text-xs font-semibold text-blue-800 dark:text-blue-300 flex items-center">
                <Sparkles className="h-3.5 w-3.5 mr-1.5 text-blue-600 dark:text-blue-400" />
                Quick Tips:
              </p>
              <ul className="text-xs text-blue-700 dark:text-blue-400 space-y-1 mt-1.5">
                <li className="flex items-center">• Type at least <strong className="mx-1">2</strong> characters for instant search</li>
                <li className="flex items-center">• Hardware USB scanners auto-detect and submit</li>
                <li className="flex items-center">• Scan member ID card barcode for fastest checkout</li>
              </ul>
            </div>
          </div>
        )}

        {/* STEP 2: BOOK SEARCH */}
        {step === 2 && memberData && (
          <div className="space-y-4 animate-slideUp">
            
            {/* Member Summary Card */}
            <div className={`bg-gradient-to-r border-2 rounded-2xl p-4 sm:p-5 shadow-md ${
              getBorrowingStatus() === 'full' 
                ? 'from-rose-50 to-red-50 dark:from-rose-900/30 dark:to-red-900/20 border-rose-200 dark:border-rose-800/60'
                : getBorrowingStatus() === 'warning'
                  ? 'from-amber-50 to-orange-50 dark:from-amber-900/30 dark:to-orange-900/20 border-amber-200 dark:border-amber-800/60'
                  : 'from-emerald-50 to-teal-50 dark:from-emerald-900/30 dark:to-teal-900/20 border-emerald-200 dark:border-emerald-800/60'
            }`}>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex items-center space-x-4 min-w-0">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-gradient-to-br from-emerald-600 to-teal-600 text-white font-bold text-lg flex items-center justify-center flex-shrink-0 shadow-lg shadow-emerald-500/20">
                    {memberData.name?.charAt(0) || 'U'}
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center flex-wrap gap-2">
                      <h3 className="font-bold text-gray-900 dark:text-white text-base sm:text-lg truncate">{memberData.name}</h3>
                      <span className={`px-2.5 py-0.5 text-xs font-semibold rounded-full capitalize ${getRoleBadgeColor(memberData.role)}`}>
                        {memberData.role}
                      </span>
                      {memberData.isActive === false && (
                        <span className="px-2.5 py-0.5 text-xs font-semibold rounded-full bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-300">
                          Inactive
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-slate-600 dark:text-slate-300 truncate font-mono">
                      {memberData.usn || memberData.libraryNumber || 'N/A'} • {memberData.email}
                    </p>
                  </div>
                </div>

                <button
                  onClick={goBack}
                  className="px-3.5 py-1.5 bg-white dark:bg-gray-800 border-2 border-slate-200 dark:border-gray-700 rounded-xl text-xs font-semibold text-slate-700 dark:text-slate-200 hover:border-blue-500 hover:text-blue-600 dark:hover:text-blue-400 transition-all flex items-center space-x-1.5"
                >
                  <ChevronLeft className="h-3.5 w-3.5" />
                  <span>Change</span>
                </button>
              </div>

              {/* Borrowing Stats */}
              {isCheckingMember ? (
                <div className="mt-4 flex items-center justify-center py-3">
                  <Loader className="h-5 w-5 animate-spin text-blue-600" />
                  <span className="ml-2 text-sm text-slate-500">Checking member status...</span>
                </div>
              ) : (
                <>
                  <div className="mt-4 pt-3 border-t border-emerald-200/60 dark:border-emerald-800/40">
                    {/* Main Stats Grid */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      <div className="bg-white/70 dark:bg-gray-800/50 rounded-xl p-2.5 text-center">
                        <p className="text-[10px] text-slate-500 dark:text-slate-400 uppercase font-semibold">Currently Issued</p>
                        <p className="text-xl font-bold text-blue-600 dark:text-blue-400">{memberStats.totalIssued}</p>
                      </div>
                      <div className="bg-white/70 dark:bg-gray-800/50 rounded-xl p-2.5 text-center">
                        <p className="text-[10px] text-slate-500 dark:text-slate-400 uppercase font-semibold">Borrowing Limit</p>
                        <p className="text-xl font-bold text-indigo-600 dark:text-indigo-400">{memberStats.maxAllowed}</p>
                      </div>
                      <div className="bg-white/70 dark:bg-gray-800/50 rounded-xl p-2.5 text-center">
                        <p className="text-[10px] text-slate-500 dark:text-slate-400 uppercase font-semibold">Remaining</p>
                        <p className={`text-xl font-bold ${memberStats.remaining > 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
                          {memberStats.remaining}
                        </p>
                      </div>
                      <div className="bg-white/70 dark:bg-gray-800/50 rounded-xl p-2.5 text-center">
                        <p className="text-[10px] text-slate-500 dark:text-slate-400 uppercase font-semibold">Overdue</p>
                        <p className={`text-xl font-bold ${memberStats.overdueCount > 0 ? 'text-rose-600 dark:text-rose-400' : 'text-slate-400 dark:text-slate-500'}`}>
                          {memberStats.overdueCount}
                        </p>
                      </div>
                    </div>

                    {/* Progress Bar */}
                    <div className="mt-3">
                      <div className="flex justify-between text-[10px] text-slate-500 dark:text-slate-400 mb-0.5">
                        <span>Borrowing Limit Usage</span>
                        <span>{memberStats.maxAllowed > 0 ? Math.round((memberStats.totalIssued / memberStats.maxAllowed) * 100) : 0}%</span>
                      </div>
                      <div className="w-full h-2 bg-slate-200 dark:bg-gray-700 rounded-full overflow-hidden">
                        <div 
                          className={`h-full rounded-full transition-all duration-500 ${
                            getBorrowingStatus() === 'full' ? 'bg-rose-500' :
                            getBorrowingStatus() === 'warning' ? 'bg-amber-500' : 'bg-emerald-500'
                          }`}
                          style={{ 
                            width: `${memberStats.maxAllowed > 0 ? Math.min((memberStats.totalIssued / memberStats.maxAllowed) * 100, 100) : 0}%` 
                          }}
                        />
                      </div>
                    </div>

                    {/* Warnings / Status Messages */}
                    <div className="flex flex-wrap gap-2 mt-3">
                      {memberStats.pendingFines > 0 && (
                        <span className="inline-flex items-center px-3 py-1.5 bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300 rounded-full text-xs font-semibold">
                          <IndianRupee className="h-3 w-3 mr-1" />
                          Pending Fine: ₹{memberStats.pendingFines}
                        </span>
                      )}
                      {memberStats.overdueCount > 0 && (
                        <span className="inline-flex items-center px-3 py-1.5 bg-rose-100 dark:bg-rose-900/30 text-rose-800 dark:text-rose-300 rounded-full text-xs font-semibold">
                          <AlertTriangle className="h-3 w-3 mr-1" />
                          {memberStats.overdueCount} Overdue Book{memberStats.overdueCount > 1 ? 's' : ''}
                        </span>
                      )}
                      {memberStats.remaining === 0 && memberStats.totalIssued > 0 && (
                        <span className="inline-flex items-center px-3 py-1.5 bg-rose-100 dark:bg-rose-900/30 text-rose-800 dark:text-rose-300 rounded-full text-xs font-semibold">
                          <AlertCircle className="h-3 w-3 mr-1" />
                          Limit Reached - Cannot Borrow More
                        </span>
                      )}
                      {memberStats.remaining > 0 && memberStats.remaining <= 1 && (
                        <span className="inline-flex items-center px-3 py-1.5 bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300 rounded-full text-xs font-semibold">
                          <AlertTriangle className="h-3 w-3 mr-1" />
                          Only {memberStats.remaining} slot remaining
                        </span>
                      )}
                      {memberStats.remaining > 0 && memberStats.remaining > 1 && memberStats.totalIssued === 0 && (
                        <span className="inline-flex items-center px-3 py-1.5 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300 rounded-full text-xs font-semibold">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Ready to borrow - {memberStats.remaining} slots available
                        </span>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Book Search Form */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl border border-slate-200 dark:border-gray-700 p-4 sm:p-6 md:p-8 shadow-lg shadow-slate-200/50 dark:shadow-none">
              <div className="flex items-center space-x-3 mb-6">
                <div className="p-2 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl">
                  <Book className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h2 className="font-bold text-gray-900 dark:text-white text-base sm:text-lg">Select Book to Issue</h2>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Scan copy barcode or search by title</p>
                </div>
              </div>

              {/* Scan Button */}
              <button
                onClick={openBookScanner}
                disabled={isBarcodeScanning || memberStats.remaining <= 0}
                className={`w-full py-3.5 sm:py-4 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 active:from-purple-800 active:to-indigo-800 disabled:opacity-50 text-white rounded-2xl font-semibold transition-all flex items-center justify-center space-x-3 shadow-lg shadow-purple-500/25 text-sm sm:text-base ${
                  memberStats.remaining <= 0 ? 'cursor-not-allowed' : ''
                }`}
              >
                {isBarcodeScanning ? (
                  <Loader className="h-5 w-5 animate-spin" />
                ) : (
                  <Scan className="h-5 w-5" />
                )}
                <span>
                  {memberStats.remaining <= 0 
                    ? 'Borrowing Limit Reached' 
                    : isBarcodeScanning 
                      ? 'Reading Scanner...' 
                      : 'Scan Book Copy Barcode'
                  }
                </span>
              </button>

              <div className="relative flex items-center justify-center my-5">
                <div className="w-full border-t border-slate-200 dark:border-gray-700"></div>
                <span className="absolute bg-white dark:bg-gray-800 px-4 text-xs font-semibold text-slate-400 uppercase tracking-wider">or search manually</span>
              </div>

              {/* Book Search Input */}
              <div className="relative" ref={bookResultsRef}>
                <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-2">
                  Search by Title, Author, or ISBN
                  {isSearching && <span className="ml-2 text-purple-500 animate-pulse font-normal">(Searching...)</span>}
                </label>

                <div className="relative group">
                  <div className="absolute left-4 top-1/2 transform -translate-y-1/2">
                    <Search className="h-4 w-4 text-slate-400 group-focus-within:text-purple-500 transition-colors" />
                  </div>
                  <input
                    ref={bookInputRef}
                    type="text"
                    placeholder={memberStats.remaining <= 0 ? "Borrowing limit reached" : "Type book title, author, or barcode..."}
                    value={bookSearch}
                    onChange={(e) => setBookSearch(e.target.value)}
                    disabled={memberStats.remaining <= 0}
                    className="w-full pl-11 pr-11 py-3.5 rounded-2xl border-2 border-slate-200 dark:border-gray-700 bg-slate-50/50 dark:bg-gray-700/50 text-gray-900 dark:text-white text-sm focus:outline-none focus:border-purple-500 focus:ring-4 focus:ring-purple-500/10 transition-all placeholder:text-slate-400 disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                  {bookSearch && (
                    <button
                      onClick={() => {
                        setBookSearch('');
                        setBookSearchResults([]);
                        setShowBookResults(false);
                        if (bookInputRef.current) bookInputRef.current.focus();
                      }}
                      className="absolute right-3.5 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 p-1.5 hover:bg-slate-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>

                {/* Book Search Dropdown */}
                {showBookResults && bookSearchResults.length > 0 && (
                  <div className="absolute z-50 left-0 right-0 mt-2 bg-white dark:bg-gray-800 rounded-2xl border-2 border-slate-200 dark:border-gray-700 shadow-2xl max-h-80 overflow-y-auto divide-y divide-slate-100 dark:divide-gray-700">
                    {bookSearchResults.map((book, index) => {
                      const isAvailable = book.availableCopies > 0;
                      return (
                        <button
                          key={book._id || index}
                          onClick={() => selectBook(book)}
                          disabled={!isAvailable || memberStats.remaining <= 0}
                          className={`w-full text-left px-4 py-3.5 transition-all flex items-center justify-between group ${
                            isAvailable && memberStats.remaining > 0
                              ? 'hover:bg-purple-50/80 dark:hover:bg-gray-700/70' 
                              : 'opacity-60 cursor-not-allowed'
                          }`}
                        >
                          <div className="flex items-start space-x-3 min-w-0 pr-2">
                            <div className={`p-2.5 rounded-xl flex-shrink-0 mt-0.5 ${
                              isAvailable && memberStats.remaining > 0
                                ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400' 
                                : 'bg-slate-100 dark:bg-gray-700 text-slate-400'
                            }`}>
                              <Book className="h-5 w-5" />
                            </div>
                            <div className="min-w-0">
                              <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                                {highlightMatch(book.title, bookSearch)}
                              </p>
                              <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                                by {highlightMatch(book.author, bookSearch)}
                              </p>
                              <div className="flex flex-wrap items-center gap-2 text-xs text-slate-400 mt-1">
                                {book.masterBarcode && (
                                  <span className="font-mono bg-slate-100 dark:bg-gray-700 px-1.5 py-0.5 rounded">
                                    {highlightMatch(book.masterBarcode, bookSearch)}
                                  </span>
                                )}
                                {book.isbn && <span>ISBN: {highlightMatch(book.isbn, bookSearch)}</span>}
                              </div>
                            </div>
                          </div>

                          <div className="flex items-center space-x-2 flex-shrink-0">
                            <span className={`px-3 py-1 text-xs font-bold rounded-full ${
                              isAvailable 
                                ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300' 
                                : 'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-300'
                            }`}>
                              {isAvailable ? `${book.availableCopies} available` : 'Out of Stock'}
                            </span>
                            {isAvailable && memberStats.remaining > 0 && (
                              <ChevronRight className="h-4 w-4 text-slate-400 group-hover:text-purple-600 transition-colors" />
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}

                {showBookResults && bookSearchResults.length === 0 && bookSearch.length >= 2 && (
                  <div className="absolute z-50 left-0 right-0 mt-2 bg-white dark:bg-gray-800 rounded-2xl border-2 border-slate-200 dark:border-gray-700 p-6 shadow-2xl text-center">
                    <div className="p-3 bg-slate-100 dark:bg-gray-700 rounded-full w-fit mx-auto mb-3">
                      <Book className="h-6 w-6 text-slate-400" />
                    </div>
                    <p className="text-sm font-semibold text-slate-600 dark:text-slate-300">No books found</p>
                    <p className="text-xs text-slate-400 mt-1">Try searching by author or master barcode</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* STEP 3: CONFIRMATION */}
        {step === 3 && memberData && bookData && (
          <div className="animate-slideUp">
            <div className="bg-white dark:bg-gray-800 rounded-2xl border border-slate-200 dark:border-gray-700 p-4 sm:p-6 md:p-8 shadow-lg shadow-slate-200/50 dark:shadow-none">
              
              {/* Header */}
              <div className="flex items-center justify-between mb-6 pb-4 border-b-2 border-slate-100 dark:border-gray-700">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-xl">
                    <CheckCircle className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h2 className="font-bold text-gray-900 dark:text-white text-base sm:text-lg">Confirm Issue</h2>
                    <p className="text-xs text-slate-500 dark:text-slate-400">Review details before confirming</p>
                  </div>
                </div>
                <button 
                  onClick={goBack} 
                  className="px-3.5 py-1.5 text-xs font-semibold text-slate-500 hover:text-purple-600 dark:text-slate-400 dark:hover:text-purple-400 bg-slate-100 dark:bg-gray-700 hover:bg-purple-50 dark:hover:bg-purple-900/30 rounded-xl transition-all flex items-center space-x-1.5"
                >
                  <ChevronLeft className="h-3.5 w-3.5" />
                  <span>Change Book</span>
                </button>
              </div>

              {/* Summary Cards Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                {/* Borrower Card */}
                <div className="p-4 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-2xl border border-blue-200 dark:border-blue-800/50">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs font-semibold text-blue-600 dark:text-blue-400 uppercase tracking-wider">Borrower</span>
                    <BadgeCheck className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-600 to-indigo-600 text-white font-bold text-lg flex items-center justify-center flex-shrink-0 shadow-lg shadow-blue-500/20">
                      {memberData.name?.charAt(0) || 'U'}
                    </div>
                    <div className="min-w-0">
                      <p className="font-bold text-gray-900 dark:text-white text-sm truncate">{memberData.name}</p>
                      <p className="text-xs text-slate-500 dark:text-slate-400 font-mono">{memberData.usn || memberData.email}</p>
                      <span className={`text-[10px] font-semibold capitalize px-2 py-0.5 rounded-full inline-block mt-1 ${getRoleBadgeColor(memberData.role)}`}>
                        {memberData.role}
                      </span>
                    </div>
                  </div>
                  <div className="mt-3 pt-2 border-t border-blue-200/50 dark:border-blue-800/30 flex items-center justify-between text-xs">
                    <span className="text-slate-500 dark:text-slate-400">Books Issued:</span>
                    <span className="font-bold text-gray-900 dark:text-white">{memberStats.totalIssued} / {memberStats.maxAllowed}</span>
                    <span className="text-slate-500 dark:text-slate-400">Remaining:</span>
                    <span className={`font-bold ${memberStats.remaining > 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
                      {memberStats.remaining}
                    </span>
                  </div>
                </div>

                {/* Book Card */}
                <div className="p-4 bg-gradient-to-br from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 rounded-2xl border border-purple-200 dark:border-purple-800/50">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs font-semibold text-purple-600 dark:text-purple-400 uppercase tracking-wider">Selected Book</span>
                    <BookOpen className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="p-2.5 bg-purple-100 dark:bg-purple-900/40 text-purple-600 dark:text-purple-400 rounded-xl flex-shrink-0">
                      <Book className="h-6 w-6" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-bold text-gray-900 dark:text-white text-sm truncate">{bookData.title}</p>
                      <p className="text-xs text-slate-500 dark:text-slate-400 truncate">by {bookData.author}</p>
                      <p className="text-[10px] font-mono text-slate-400 dark:text-slate-500 mt-0.5">
                        {bookData.masterBarcode}
                      </p>
                    </div>
                  </div>
                  <div className="mt-3 pt-2 border-t border-purple-200/50 dark:border-purple-800/30 flex items-center justify-between text-xs">
                    <span className="text-slate-500 dark:text-slate-400">Available Copies:</span>
                    <span className="font-bold text-emerald-600 dark:text-emerald-400">{bookData.availableCopies}</span>
                    <span className="text-slate-500 dark:text-slate-400">Total:</span>
                    <span className="font-bold text-gray-900 dark:text-white">{bookData.totalCopies}</span>
                  </div>
                </div>
              </div>

              {/* Copy Selector */}
              {availableCopies.length > 0 && (
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-3">
                    <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                      Select Copy to Issue
                    </label>
                    <span className="text-xs font-medium text-emerald-600 dark:text-emerald-400">
                      {availableCopies.length} copy{availableCopies.length > 1 ? 's' : ''} available
                    </span>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2.5">
                    {availableCopies.map((copy) => {
                      const isSelected = selectedCopy?.copyNumber === copy.copyNumber;
                      const status = getCopyStatus(copy);
                      
                      return (
                        <button
                          type="button"
                          key={copy.copyNumber}
                          onClick={() => selectCopy(copy)}
                          className={`
                            relative p-3 rounded-xl border-2 text-left transition-all duration-200
                            ${isSelected 
                              ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/40 shadow-lg shadow-emerald-500/20 scale-[1.02] ring-4 ring-emerald-500/20' 
                              : 'border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-emerald-300 dark:hover:border-emerald-700 hover:shadow-md'
                            }
                          `}
                        >
                          {isSelected && (
                            <div className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-emerald-500 text-white flex items-center justify-center shadow-lg shadow-emerald-500/30">
                              <Check className="h-3.5 w-3.5" />
                            </div>
                          )}

                          <div className="flex flex-col items-center text-center">
                            <div className={`p-2 rounded-lg mb-1.5 transition-colors ${
                              isSelected 
                                ? 'bg-emerald-100 dark:bg-emerald-900/50 text-emerald-600 dark:text-emerald-400' 
                                : 'bg-slate-100 dark:bg-gray-700 text-slate-500'
                            }`}>
                              <Copy className="h-4 w-4" />
                            </div>
                            <p className={`text-xs font-bold ${isSelected ? 'text-emerald-700 dark:text-emerald-300' : 'text-gray-700 dark:text-gray-300'}`}>
                              Copy #{copy.copyNumber}
                            </p>
                            <p className="text-[10px] font-mono text-slate-400 dark:text-slate-500 mt-0.5 truncate max-w-full">
                              {copy.barcode}
                            </p>
                            <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full mt-1.5 ${status.color}`}>
                              {status.label}
                            </span>
                          </div>

                          {isSelected && (
                            <div className="absolute inset-0 rounded-xl border-2 border-emerald-500 pointer-events-none animate-pulse" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Issue Settings */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6 p-4 bg-slate-50 dark:bg-gray-700/30 rounded-2xl border border-slate-200 dark:border-gray-700">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-2">
                    <Calendar className="h-3.5 w-3.5 inline mr-1.5" />
                    Borrow Period
                  </label>
                  <div className="flex items-center space-x-3">
                    <input
                      type="number"
                      value={dueDays}
                      onChange={(e) => setDueDays(parseInt(e.target.value) || 1)}
                      className="w-24 px-3 py-2.5 rounded-xl border-2 border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm font-semibold focus:outline-none focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10 transition-all"
                      min="1"
                      max="90"
                    />
                    <span className="text-xs text-slate-500 dark:text-slate-400">
                      days
                      <span className="block text-[10px] text-slate-400 dark:text-slate-500 mt-0.5">
                        Due: <strong className="text-slate-700 dark:text-slate-300">{new Date(Date.now() + dueDays * 86400000).toLocaleDateString('en-IN')}</strong>
                      </span>
                    </span>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-2">
                    <Info className="h-3.5 w-3.5 inline mr-1.5" />
                    Issue Notes
                  </label>
                  <input
                    type="text"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="e.g. Minor cover crease..."
                    className="w-full px-3.5 py-2.5 rounded-xl border-2 border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10 transition-all placeholder:text-slate-400"
                  />
                </div>
              </div>

              {/* Rules Summary */}
              {rules && (
                <div className="mb-6 p-4 bg-gradient-to-r from-blue-50/80 to-indigo-50/80 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-2xl border border-blue-200 dark:border-blue-800/50">
                  <div className="flex flex-wrap items-center gap-4 text-xs text-blue-800 dark:text-blue-300">
                    <span className="flex items-center font-medium">
                      <Shield className="h-3.5 w-3.5 mr-1.5 text-blue-600 dark:text-blue-400" />
                      Grace: {rules.gracePeriodDays || 2} days
                    </span>
                    <span className="flex items-center font-medium">
                      <IndianRupee className="h-3.5 w-3.5 mr-0.5 text-blue-600 dark:text-blue-400" />
                      Fine: ₹{rules.finePerDay || 2}/day after grace
                    </span>
                    <span className="flex items-center font-medium">
                      <RefreshCw className="h-3.5 w-3.5 mr-1.5 text-blue-600 dark:text-blue-400" />
                      Max Renewals: {rules.maxRenewals || 2}
                    </span>
                    <span className="flex items-center font-medium">
                      <BookOpen className="h-3.5 w-3.5 mr-1.5 text-blue-600 dark:text-blue-400" />
                      Max Books: {rules.maxBooksAllowed || 5}
                    </span>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-4 pt-4 border-t-2 border-slate-100 dark:border-gray-700">
                <button
                  onClick={resetForm}
                  className="px-6 py-3 border-2 border-slate-200 dark:border-gray-700 hover:border-rose-300 dark:hover:border-rose-700 hover:bg-rose-50 dark:hover:bg-rose-900/20 text-slate-700 dark:text-slate-300 rounded-2xl text-sm font-semibold transition-all flex items-center justify-center space-x-2"
                >
                  <X className="h-4 w-4" />
                  <span>Cancel</span>
                </button>
                
                <button
                  onClick={handleIssue}
                  disabled={issuing || !canBorrow() || (availableCopies.length > 1 && !selectedCopy) || memberStats.remaining <= 0}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 active:from-emerald-800 active:to-teal-800 disabled:opacity-50 text-white rounded-2xl text-sm font-semibold transition-all flex items-center justify-center space-x-2 shadow-lg shadow-emerald-500/25"
                >
                  {issuing ? (
                    <>
                      <Loader className="h-4 w-4 animate-spin" />
                      <span>Processing...</span>
                    </>
                  ) : memberStats.remaining <= 0 ? (
                    <>
                      <AlertCircle className="h-4 w-4" />
                      <span>Limit Reached</span>
                    </>
                  ) : (
                    <>
                      <CheckCircle className="h-4 w-4" />
                      <span>Confirm & Issue Book</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Barcode Scanner Modal */}
        {showScanner && (
          <BarcodeScanner
            onScan={handleBarcodeScan}
            onClose={() => {
              setShowScanner(false);
              setIsBarcodeScanning(false);
            }}
            title={scanType === 'member' ? 'Scan Member Identity Card' : 'Scan Book Copy Barcode'}
            description={scanType === 'member' 
              ? 'Point camera or use hardware scanner over the student USN/Barcode' 
              : 'Point camera or use hardware scanner over the book spine barcode'
            }
            enableHardware={true}
            enableCamera={true}
          />
        )}
      </div>

      {/* Animation Keyframes */}
      <style jsx>{`
        @keyframes slideDown {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-slideDown {
          animation: slideDown 0.3s ease-out;
        }
        .animate-slideUp {
          animation: slideUp 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default IssueBook;